import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  TrendingUp, 
  TrendingDown,
  Lightbulb,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  Calendar,
  BarChart3,
  Sparkles
} from 'lucide-react';
import { generateProgressInsights } from '@/utils/insightGenerator';

/**
 * Progress Tracker - Turns user data into simple personalized insights
 * Shows patterns, weekly summary, and actionable suggestions
 */

const ProgressTracker = () => {
  const navigate = useNavigate();
  const [insightsData, setInsightsData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Generate insights on load
    // In production, this would fetch from backend API
    setTimeout(() => {
      const data = generateProgressInsights();
      setInsightsData(data);
      setLoading(false);
    }, 500);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Analyzing your health patterns...</p>
        </div>
      </div>
    );
  }

  if (!insightsData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Not enough data yet. Check in daily to see your patterns!</p>
            <Button onClick={() => navigate('/daily-loop')} className="mt-4">
              Go to Daily Health Loop
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { patterns, summary, suggestions } = insightsData;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 pb-20">
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center mb-3">
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-full p-3">
              <BarChart3 className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">
            Your Progress Tracker
          </h1>
          <p className="text-gray-600">Understanding your health patterns</p>
          
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mt-3">
            <Calendar className="h-4 w-4" />
            <span>Last 7 days</span>
          </div>
        </div>

        {/* Personal Insights - Pattern Recognition */}
        <Card className="border-2 border-purple-200 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-purple-900">
              <Sparkles className="h-6 w-6 text-purple-600" />
              Your Personal Insights
            </CardTitle>
            <p className="text-sm text-gray-600 mt-1">
              Patterns we've noticed in your health data
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {patterns.length > 0 ? (
              patterns.map((insight, index) => (
                <div
                  key={index}
                  className={`
                    p-4 rounded-lg border-2 transition-all
                    ${insight.severity === 'positive' 
                      ? 'bg-green-50 border-green-200' 
                      : insight.severity === 'medium'
                      ? 'bg-blue-50 border-blue-200'
                      : 'bg-purple-50 border-purple-200'
                    }
                  `}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-3xl flex-shrink-0">{insight.icon}</span>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900 mb-1">
                        {insight.text}
                      </p>
                      <p className="text-sm text-gray-600">
                        {insight.detail}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-6 text-gray-500">
                <Lightbulb className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                <p className="text-sm">
                  Keep tracking for a few more days to see your patterns
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Weekly Summary */}
        <div className="grid md:grid-cols-2 gap-4">
          
          {/* What's Improving */}
          <Card className="border-2 border-green-200 bg-green-50 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-900 text-lg">
                <TrendingUp className="h-5 w-5 text-green-600" />
                What's Improving
              </CardTitle>
            </CardHeader>
            <CardContent>
              {summary.improvements.length > 0 ? (
                <div className="space-y-3">
                  {summary.improvements.map((item, index) => (
                    <div key={index} className="flex items-start gap-3 bg-white p-3 rounded-lg border border-green-200">
                      <span className="text-2xl flex-shrink-0">{item.icon}</span>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{item.text}</p>
                        <p className="text-xs text-gray-600 mt-0.5">{item.detail}</p>
                      </div>
                      <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-green-800 text-center py-4">
                  Keep going! Improvements will show as you track more days
                </p>
              )}
            </CardContent>
          </Card>

          {/* Needs Attention */}
          <Card className="border-2 border-orange-200 bg-orange-50 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-orange-900 text-lg">
                <TrendingDown className="h-5 w-5 text-orange-600" />
                Needs Attention
              </CardTitle>
            </CardHeader>
            <CardContent>
              {summary.needsAttention.length > 0 ? (
                <div className="space-y-3">
                  {summary.needsAttention.map((item, index) => (
                    <div key={index} className="flex items-start gap-3 bg-white p-3 rounded-lg border border-orange-200">
                      <span className="text-2xl flex-shrink-0">{item.icon}</span>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{item.text}</p>
                        <p className="text-xs text-gray-600 mt-0.5">{item.detail}</p>
                      </div>
                      <AlertCircle className="h-5 w-5 text-orange-600 flex-shrink-0" />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-orange-800 text-center py-4">
                  Great! No major areas need attention right now
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Actionable Suggestions */}
        <Card className="border-2 border-blue-200 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-900">
              <Lightbulb className="h-6 w-6 text-blue-600" />
              Suggested Actions
            </CardTitle>
            <p className="text-sm text-gray-600 mt-1">
              Small steps you can take this week
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {suggestions.map((suggestion, index) => (
              <div key={index} className="bg-gradient-to-r from-blue-50 to-purple-50 p-5 rounded-lg border-2 border-blue-200">
                <div className="flex items-start gap-4">
                  <div className="bg-white rounded-full p-3 flex-shrink-0">
                    <span className="text-2xl">{suggestion.icon}</span>
                  </div>
                  
                  <div className="flex-1 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-semibold text-gray-900 text-lg">
                        {suggestion.title}
                      </h3>
                      {suggestion.priority === 'high' && (
                        <span className="bg-orange-100 text-orange-700 text-xs font-medium px-2 py-1 rounded-full flex-shrink-0">
                          High Priority
                        </span>
                      )}
                    </div>
                    
                    <div className="bg-white p-3 rounded-lg border border-blue-200">
                      <div className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-blue-900">
                            {suggestion.action}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            {suggestion.why}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Info Card */}
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <Sparkles className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-purple-900">
                <strong>How this works:</strong> We analyze your daily check-ins to find patterns 
                between your habits (sleep, water, movement) and how you feel. 
                The more you track, the better insights you'll get!
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button
            onClick={() => navigate('/daily-loop')}
            className="flex-1 h-12 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
          >
            Continue Daily Loop
          </Button>
          <Button
            onClick={() => navigate('/dashboard')}
            variant="outline"
            className="flex-1 h-12 border-2 border-purple-300 hover:bg-purple-50"
          >
            Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProgressTracker;
